/**
 * Nutrition Access / Welfare Guardrails
 *
 * Single source of truth for *who can see what* in the nutrition feature.
 *
 * Adolescent athletes are a recognised higher-risk group for disordered eating
 * and RED-S, so body-weight entry and energy/calorie figures are deliberately
 * restricted by development phase. See docs:
 *   - energy-fuelling-estimator-handoff.md
 *   - fuelling-for-performance-content.md
 *
 * Rules enforced here:
 *   - Foundation phase (U11–U15 players): NO weight entry, NO calorie/energy
 *     numbers, NO weight-trend charts. Food/timing *guidance* is still safe.
 *   - Performance phase (U16+ players): may enter weight and see fuelling
 *     numbers, but numbers must be framed as a floor ("eat at least"), and
 *     weight-trend charts remain staff-only.
 *   - Weight-trend charts are ALWAYS staff-facing only — never any player.
 *
 * Fail-safe: if a player's age cannot be determined (no DOB), they are treated
 * as Foundation (most restrictive). Numbers are hidden by default, never shown.
 */

import { computed } from "vue";
import store from "@/store";

// Performance phase starts at U16. Below this, players are Foundation phase.
export const PERFORMANCE_MIN_AGE = 16;

// Phase is decided by FOOTBALL BAND AGE — the age a player reaches during the
// current calendar year — not their literal birthday-adjusted age. Scottish
// youth squads band by birth year: a 2010 player is U16 for 2025/26 (=
// Performance) even though their Oct birthday means they're still 15. So a
// 2010-born child resolves to band age 16 from Jan of that year.

/**
 * Band age from the `age_group` field. It holds a birth YEAR (e.g. "2010") in
 * this system; a "U16"-style squad label is also tolerated. Null if
 * unrecognised (e.g. "0000").
 */
export function bandAgeFromAgeGroup(ageGroup) {
  if (!ageGroup) return null;
  const match = String(ageGroup).match(/(\d+)/);
  if (!match) return null;
  const n = parseInt(match[1], 10);
  if (isNaN(n)) return null;
  const currentYear = new Date().getFullYear();
  if (n >= 1990 && n <= currentYear) return currentYear - n; // birth year
  if (n >= 5 && n <= 30) return n; // squad age label, e.g. "U16"
  return null;
}

/** Band age from a DOB value (current year − birth year). Null if invalid. */
export function bandAgeFromDob(dob) {
  if (!dob || dob === "" || dob === "0000-00-00") return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  return new Date().getFullYear() - birth.getFullYear();
}

/**
 * Pure resolver. Pass a profile object (e.g. store getProfile / userData) and
 * get back the access flags. Safe to call from Options-API computed properties.
 */
export function getNutritionAccess(profile) {
  const p = profile || {};

  const userType = p.userType;
  const position = p.position;
  const isStaff =
    userType === "Admin" ||
    userType === "Coach" ||
    position === "Coach" ||
    position === "HeadCoach";
  const isPlayer = !isStaff;

  // age_group (the club's squad banding) is authoritative; DOB's year is the
  // fallback. Both yield band age (current year − birth year).
  const age = bandAgeFromAgeGroup(p.age_group) ?? bandAgeFromDob(p.dob);

  // Players only. Unknown age => fail safe to Foundation (most restrictive).
  const isPerformance = isPlayer && age != null && age >= PERFORMANCE_MIN_AGE;
  const isFoundation = isPlayer && !isPerformance;

  const phase = isStaff
    ? "staff"
    : isPerformance
      ? "performance"
      : "foundation";

  return {
    age,
    phase,
    isStaff,
    isPlayer,
    isFoundation,
    isPerformance,

    // Capabilities -----------------------------------------------------------
    // Energy/calorie/macro numbers. Staff (monitoring) + Performance players.
    canSeeEnergyFigures: isStaff || isPerformance,
    // Self body-weight entry. Performance players only (or staff entering FOR a player).
    canEnterWeight: isStaff || isPerformance,
    // Weight-trend chart. STAFF ONLY — never any player, even Performance.
    canSeeWeightTrend: isStaff,

    // Copy framing for any number shown to a player: a floor, never a target.
    energyFraming: isStaff ? "neutral" : "floor",
  };
}

/**
 * Composition-API wrapper. Reactive against the Vuex profile.
 */
export function useNutritionAccess() {
  const access = computed(() =>
    getNutritionAccess(store.getters.getProfile || {})
  );

  return {
    access,
    age: computed(() => access.value.age),
    phase: computed(() => access.value.phase),
    isStaff: computed(() => access.value.isStaff),
    isPlayer: computed(() => access.value.isPlayer),
    isFoundation: computed(() => access.value.isFoundation),
    isPerformance: computed(() => access.value.isPerformance),
    canSeeEnergyFigures: computed(() => access.value.canSeeEnergyFigures),
    canEnterWeight: computed(() => access.value.canEnterWeight),
    canSeeWeightTrend: computed(() => access.value.canSeeWeightTrend),
    energyFraming: computed(() => access.value.energyFraming),
  };
}

export default useNutritionAccess;
